import { useState, useCallback, useRef, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Video,
  VideoOff,
  SwitchCamera,
  Zap,
  Wifi,
  WifiOff,
  AlertCircle,
  Upload,
  Camera,
  GraduationCap,
  Briefcase,
  Award,
  Loader2,
  FolderOpen,
  FileText,
  Send,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  AlertTriangle,
  FileCheck
} from 'lucide-react';
import api from '@/lib/api';
import { useGeminiLive, type UserLevel } from '@/hooks/useGeminiLive';
import { GlassPanel } from '@/components/layout';
import { Transcript } from '@/components/voice';
import { cn } from '@/lib/cn';
import { fadeIn, slideIn, staggerContainer } from '@/lib/motion';
import { MissionPanel } from './MissionPanel';
import {
  useDocumentContextStore,
  MAX_CONTEXT_CHARACTERS,
  WARNING_THRESHOLD
} from '@/stores/documentContextStore';

/**
 * Live Voice Tab - Real-time Voice Interface
 *
 * Features:
 * - Ultra-low latency (~100-300ms)
 * - Native voice activity detection
 * - Real-time video input
 * - Built-in transcription
 */
export function LiveVoiceTab() {
  const [userLevel, setUserLevel] = useState<UserLevel>('junior');
  const [isMissionPanelOpen, setIsMissionPanelOpen] = useState(false);
  const [textInput, setTextInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const {
    documents,
    totalCharacters,
    selectedDocumentIds,
    selectedCharacters,
    addDocument,
    toggleDocumentSelection,
    selectAllDocuments,
    clearSelection,
    getSelectionStatus,
  } = useDocumentContextStore();

  const [isContextExpanded, setIsContextExpanded] = useState(false);
  const selectionStatus = getSelectionStatus();

  const {
    status,
    error,
    volume,
    isMuted,
    isVoiceEnabled,
    isVideoActive,
    facingMode,
    hasMultipleCameras,
    messages,
    pendingReviews,
    acknowledgeMessage,
    submitFeedback,
    canDisconnect,
    connect,
    disconnect,
    toggleMute,
    enableVoice,
    disableVoice,
    startVideo,
    stopVideo,
    switchCamera,
    sendImage,
    capturePhoto, // BUG-032
    sendText,
    sendDocumentContext, // BUG-021: Send document context to active session
    previewVideoRef,
    // Idle timeout (BUG-011 fix)
    idleWarning,
    resetIdleTimer,
  } = useGeminiLive({
    userLevel,
  });
  
  const isLive = status === 'connected' || status === 'reconnecting';
  
  const handleConnect = useCallback(() => {
    connect();
  }, [connect]);
  
  const [isCapturing, setIsCapturing] = useState(false);

  // BUG-032: Process an image (from camera capture or file upload) through
  // backend OCR and store in document context. Also sends to Gemini for
  // immediate visual analysis if connected.
  const processImageToContext = useCallback(async (file: File | Blob, name: string) => {
    setIsCapturing(true);
    try {
      // 1. Send to Gemini for immediate visual analysis (if connected)
      if (isLive && sendImage) {
        const imageFile = file instanceof File ? file : new File([file], name, { type: 'image/jpeg' });
        sendImage(imageFile);
      }

      // 2. Upload to backend for OCR text extraction → context store
      const formData = new FormData();
      const uploadFile = file instanceof File ? file : new File([file], name, { type: 'image/jpeg' });
      formData.append('file', uploadFile);

      const response = await api.post('/documents/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const result = response.data;
      if (result.success && result.extracted_text) {
        addDocument({
          id: `${Date.now()}-${name}`,
          name,
          type: 'image',
          size: uploadFile.size,
          uploadedAt: new Date(),
          extractedText: result.extracted_text,
        });

        // Trigger reconnection to include new context in system prompt
        if (isLive && sendDocumentContext) {
          sendDocumentContext();
        }
      }
    } catch (err) {
      console.error("Failed to process image:", err);
    } finally {
      setIsCapturing(false);
    }
  }, [isLive, sendImage, addDocument, sendDocumentContext]);

  // BUG-032: Take a picture from the active camera
  const handleTakePicture = useCallback(async () => {
    const blob = capturePhoto();
    if (!blob) return;
    const name = `photo-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}.jpg`;
    await processImageToContext(blob, name);
  }, [capturePhoto, processImageToContext]);

  // BUG-032: Upload an image file for OCR + context
  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await processImageToContext(file, file.name);
    }
    e.target.value = '';
  }, [processImageToContext]);

  const [isSending, setIsSending] = useState(false);

  const handleTextSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!textInput.trim() || isSending) return;

    setIsSending(true);
    try {
      await sendText(textInput);
      setTextInput('');
    } finally {
      setIsSending(false);
    }
  }, [textInput, isSending, sendText]);

  // Handle feedback submission (BUG-002 fix)
  const handleFeedback = useCallback(async (messageId: string, feedback: 'positive' | 'negative') => {
    // Update local state immediately for responsiveness
    submitFeedback(messageId, feedback);

    // Call backend API to persist feedback
    try {
      const token = localStorage.getItem('dryvox_access_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
      await fetch(`${apiUrl}/voice-live/messages/${messageId}/feedback?feedback=${feedback}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Local state already updated, so user still sees feedback
    }
  }, [submitFeedback]);

  const getStatusText = () => {
    switch (status) {
      case 'disconnected': return userLevel ? 'Ready to Connect' : 'Select Level';
      case 'connecting': return 'Connecting...';
      case 'connected': return `Live (${userLevel})`;
      case 'reconnecting': return 'Reconnecting...';
      case 'error': return 'Connection Error';
      default: return 'Unknown';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connected': return 'text-emerald-400';
      case 'connecting':
      case 'reconnecting': return 'text-amber-400';
      case 'error': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col items-center py-6"
    >
      {/* Voice Mode Badge */}
      <motion.div
        variants={fadeIn}
        className="mb-6 flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-ocean-cyan/20 to-ocean-blue/20 border border-ocean-cyan/30"
      >
        <Zap className="w-4 h-4 text-ocean-cyan" />
        <span className="text-sm font-medium text-ocean-cyan">
          DryVox Voice
        </span>
        <span className="text-xs text-ocean-cyan/60">
          Real-time assistant
        </span>
      </motion.div>

      {/* Experience Level Selector (when disconnected) */}
      {!isLive && (
        <motion.div
          variants={slideIn('up')}
          className="mb-8 w-full max-w-md"
        >
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest text-center mb-4">
            Experience Level
          </p>
          <div className="flex gap-3 justify-center">
            {(['trainee', 'junior', 'senior'] as const).map((level) => (
              <button
                key={level}
                onClick={() => setUserLevel(level)}
                className={cn(
                  'flex-1 max-w-[120px] py-4 rounded-2xl flex flex-col items-center gap-2 transition-all',
                  userLevel === level
                    ? 'bg-gradient-to-br from-ocean-cyan/20 to-ocean-blue/20 border border-ocean-cyan/40 text-ocean-cyan shadow-lg shadow-ocean-cyan/10'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                )}
              >
                {level === 'trainee' && <GraduationCap className="w-5 h-5" />}
                {level === 'junior' && <Briefcase className="w-5 h-5" />}
                {level === 'senior' && <Award className="w-5 h-5" />}
                <span className="text-xs font-bold uppercase tracking-wider capitalize">
                  {level}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3 max-w-md w-full"
        >
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-300">{error}</p>
        </motion.div>
      )}

      {/* Main Visualizer */}
      <motion.div
        variants={slideIn('up')}
        className={cn(
          'relative w-full max-w-lg p-8 rounded-3xl transition-all duration-500',
          isLive 
            ? 'bg-gradient-to-b from-slate-800/60 to-slate-900/60 border border-violet-500/20' 
            : 'bg-white/5 border border-white/10'
        )}
      >
        {/* Video Preview (when active) */}
        <AnimatePresence>
          {isVideoActive && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute top-4 right-4 w-32 md:w-40 rounded-xl overflow-hidden shadow-2xl border-2 border-white/10 z-20"
            >
              <video
                ref={previewVideoRef}
                autoPlay
                playsInline
                muted
                className={cn(
                  'w-full h-full object-cover bg-black',
                  facingMode === 'user' && 'transform -scale-x-100'
                )}
              />
              {hasMultipleCameras && (
                <button
                  onClick={switchCamera}
                  className="absolute bottom-2 right-2 p-2.5 bg-black/60 text-white rounded-full hover:bg-black/80 backdrop-blur-sm active:scale-95 transition-transform"
                >
                  <SwitchCamera className="w-4 h-4" />
                </button>
              )}
              <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/90 backdrop-blur-md">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[8px] font-bold text-white tracking-wider">LIVE</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Voice Waveform Visualization */}
        <div className="flex items-center justify-center gap-1 h-28 mb-8">
          {[...Array(35)].map((_, i) => (
            <motion.div
              key={i}
              className={cn(
                'w-1 rounded-full transition-colors',
                isLive 
                  ? 'bg-gradient-to-t from-violet-500 to-fuchsia-400' 
                  : 'bg-slate-600'
              )}
              animate={{
                height: isLive 
                  ? [
                      Math.random() * 15 + 8,
                      Math.random() * 50 + (volume * 100),
                      Math.random() * 15 + 8
                    ]
                  : 8
              }}
              transition={{
                duration: 0.6 + Math.random() * 0.3,
                repeat: Infinity,
                delay: i * 0.02
              }}
            />
          ))}
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-center gap-8">
          {/* Mute Button */}
          <button
            onClick={toggleMute}
            disabled={!isLive}
            className={cn(
              'p-4 rounded-full transition-all duration-200',
              isMuted
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white hover:border-white/20',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>

          {/* Connect/Disconnect Button */}
          <div className="relative">
            <button
              onClick={isLive ? disconnect : handleConnect}
              disabled={!userLevel && !isLive}
              title={isLive && !canDisconnect ? `${pendingReviews} items require review before disconnecting` : undefined}
              className={cn(
                'p-6 rounded-full transition-all duration-300 relative',
                isLive
                  ? !canDisconnect
                    ? 'bg-amber-500/20 text-amber-400 border-2 border-amber-500/40 hover:bg-amber-500/30'
                    : 'bg-red-500/20 text-red-400 border-2 border-red-500/40 hover:bg-red-500/30'
                  : !userLevel
                    ? 'bg-slate-800 text-slate-600 border-2 border-slate-700 cursor-not-allowed'
                    : 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white border-2 border-violet-400/50 hover:from-violet-400 hover:to-fuchsia-400 shadow-lg shadow-violet-500/25'
              )}
            >
              {status === 'connecting' || status === 'reconnecting' ? (
                <Loader2 className="w-10 h-10 animate-spin" />
              ) : isLive ? (
                <PhoneOff className="w-10 h-10" />
              ) : (
                <Phone className="w-10 h-10 fill-current" />
              )}

              {/* Pulse effect when connected */}
              {isLive && (
                <motion.div
                  animate={{ scale: [1, 1.3], opacity: [0.5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute inset-0 rounded-full border-2 border-violet-400"
                />
              )}
            </button>

            {/* Warning badge when pending reviews */}
            {isLive && !canDisconnect && (
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">
                {pendingReviews}
              </div>
            )}
          </div>

          {/* Video Button */}
          <button
            onClick={() => isVideoActive ? stopVideo() : startVideo()}
            disabled={!isLive}
            className={cn(
              'p-4 rounded-full transition-all duration-200',
              isVideoActive
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white hover:border-white/20',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isVideoActive ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>
        </div>

        {/* Status Text */}
        <div className="mt-6 text-center">
          <div className={cn('flex items-center justify-center gap-2 text-sm font-semibold', getStatusColor())}>
            {status === 'connected' ? (
              <Wifi className="w-4 h-4" />
            ) : status === 'error' ? (
              <WifiOff className="w-4 h-4" />
            ) : status === 'connecting' || status === 'reconnecting' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            {getStatusText()}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex justify-center gap-3">
          {/* Mission Data Button */}
          <button
            onClick={() => setIsMissionPanelOpen(true)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-xl border transition-all text-sm',
              documents.length > 0
                ? 'bg-violet-500/20 border-violet-500/30 text-violet-300 hover:bg-violet-500/30'
                : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
            )}
          >
            <FolderOpen className="w-4 h-4" />
            <span>Mission Context</span>
            {documents.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-violet-500/30 text-xs font-bold">
                {documents.length}
              </span>
            )}
          </button>
          
          {/* BUG-032: Take Picture (camera snap) + Upload Image → OCR → context */}
          {isLive && (
            <>
              {isVideoActive && (
                <button
                  onClick={handleTakePicture}
                  disabled={isCapturing}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-all text-sm disabled:opacity-50"
                  title="Capture photo from camera for context"
                >
                  {isCapturing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  Take Picture
                </button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isCapturing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-all text-sm disabled:opacity-50"
                title="Upload image for OCR and context"
              >
                <Upload className="w-4 h-4" />
                Upload Image
              </button>
            </>
          )}
        </div>
        
      </motion.div>

      {/* Context Files Selector - Below Voice Orb */}
      {documents.length > 0 && (
        <motion.div
          variants={slideIn('up')}
          className="w-full max-w-lg mt-4"
        >
          <GlassPanel className="overflow-hidden">
            {/* Header - Always visible */}
            <button
              onClick={() => setIsContextExpanded(!isContextExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-medium text-white">
                  Context Files
                </span>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-bold',
                  selectedDocumentIds.length > 0
                    ? 'bg-violet-500/30 text-violet-300'
                    : 'bg-white/10 text-slate-400'
                )}>
                  {selectedDocumentIds.length} / {documents.length} selected
                </span>
              </div>
              <div className="flex items-center gap-3">
                {/* Character count indicator */}
                <div className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs',
                  selectionStatus === 'danger'
                    ? 'bg-red-500/20 text-red-300'
                    : selectionStatus === 'warning'
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'bg-emerald-500/20 text-emerald-300'
                )}>
                  {selectionStatus === 'danger' && <AlertTriangle className="w-3 h-3" />}
                  {selectionStatus === 'warning' && <AlertCircle className="w-3 h-3" />}
                  {selectionStatus === 'safe' && <Check className="w-3 h-3" />}
                  <span>{(selectedCharacters / 1000).toFixed(0)}k / 200k</span>
                </div>
                {isContextExpanded ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </div>
            </button>

            {/* Expandable Content */}
            <AnimatePresence>
              {isContextExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {/* Warning/Error Banner */}
                  {selectionStatus !== 'safe' && (
                    <div className={cn(
                      'px-4 py-2 text-xs flex items-center gap-2',
                      selectionStatus === 'danger'
                        ? 'bg-red-500/10 text-red-300 border-t border-red-500/20'
                        : 'bg-amber-500/10 text-amber-300 border-t border-amber-500/20'
                    )}>
                      {selectionStatus === 'danger' ? (
                        <>
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <span>Limit exceeded! Responses may be less accurate or incomplete. Please deselect some files.</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>Approaching limit. Consider deselecting large files for better accuracy.</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="px-4 py-2 flex items-center justify-between border-t border-white/5">
                    <span className="text-xs text-slate-500">Select files to include as context</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAllDocuments}
                        className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        Select All
                      </button>
                      <span className="text-slate-600">|</span>
                      <button
                        onClick={clearSelection}
                        className="text-xs text-slate-400 hover:text-slate-300 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* File List */}
                  <div className="px-2 py-2 max-h-48 overflow-y-auto border-t border-white/5">
                    {documents.map((doc) => {
                      const isSelected = selectedDocumentIds.includes(doc.id);
                      const docChars = doc.extractedText?.length || 0;
                      return (
                        <button
                          key={doc.id}
                          onClick={() => toggleDocumentSelection(doc.id)}
                          className={cn(
                            'w-full px-3 py-2 rounded-lg flex items-center gap-3 transition-all mb-1',
                            isSelected
                              ? 'bg-violet-500/20 border border-violet-500/30'
                              : 'bg-white/5 border border-transparent hover:border-white/10'
                          )}
                        >
                          {/* Checkbox */}
                          <div className={cn(
                            'w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors',
                            isSelected
                              ? 'bg-violet-500 text-white'
                              : 'bg-white/10 text-transparent'
                          )}>
                            <Check className="w-3 h-3" />
                          </div>

                          {/* File Info */}
                          <div className="flex-1 text-left min-w-0">
                            <p className={cn(
                              'text-sm truncate',
                              isSelected ? 'text-white' : 'text-slate-300'
                            )}>
                              {doc.name}
                            </p>
                          </div>

                          {/* Character Count */}
                          <span className={cn(
                            'text-xs flex-shrink-0',
                            isSelected ? 'text-violet-300' : 'text-slate-500'
                          )}>
                            {(docChars / 1000).toFixed(1)}k
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Progress Bar */}
                  <div className="px-4 py-3 border-t border-white/5">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all duration-300 rounded-full',
                          selectionStatus === 'danger'
                            ? 'bg-gradient-to-r from-red-500 to-red-400'
                            : selectionStatus === 'warning'
                              ? 'bg-gradient-to-r from-amber-500 to-amber-400'
                              : 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                        )}
                        style={{ width: `${Math.min((selectedCharacters / MAX_CONTEXT_CHARACTERS) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-slate-500">
                        {(selectedCharacters / 1000).toFixed(0)}k / {(MAX_CONTEXT_CHARACTERS / 1000).toFixed(0)}k characters
                      </span>
                      {selectedCharacters > WARNING_THRESHOLD && selectedCharacters <= MAX_CONTEXT_CHARACTERS && (
                        <span className="text-xs text-amber-400">
                          {((MAX_CONTEXT_CHARACTERS - selectedCharacters) / 1000).toFixed(0)}k remaining
                        </span>
                      )}
                      {selectedCharacters > MAX_CONTEXT_CHARACTERS && (
                        <span className="text-xs text-red-400">
                          {((selectedCharacters - MAX_CONTEXT_CHARACTERS) / 1000).toFixed(0)}k over limit
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </GlassPanel>
        </motion.div>
      )}

      {/* Transcript Panel */}
      <motion.div
        variants={slideIn('up')}
        className="w-full max-w-2xl mt-8"
      >
        <GlassPanel className="p-2">
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
            <h3 className="text-sm font-medium text-white/70">Live Transcript</h3>
            <div className="flex items-center gap-3">
              {/* Idle warning (BUG-011 fix) */}
              {idleWarning && (
                <button
                  onClick={resetIdleTimer}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-orange-500/20 border border-orange-500/30 hover:bg-orange-500/30 transition-colors animate-pulse"
                  title="Click to stay connected"
                >
                  <AlertCircle className="w-3 h-3 text-orange-400" />
                  <span className="text-xs text-orange-300">Auto-pause in 1 min</span>
                </button>
              )}
              {/* Pending reviews warning */}
              {pendingReviews > 0 && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
                  <AlertCircle className="w-3 h-3 text-amber-400" />
                  <span className="text-xs text-amber-300">{pendingReviews} to review</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-violet-400">
                <Zap className="w-3 h-3" />
                Real-time
              </div>
            </div>
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm italic">
                {isLive
                  ? "Listening... Start speaking to DryVox"
                  : "Connect to start a conversation"}
              </p>
            </div>
          ) : (
            <Transcript
              messages={messages}
              maxHeight="300px"
              showConfidence={true}
              onAcknowledge={acknowledgeMessage}
              onFeedback={handleFeedback}
            />
          )}

          {/* Text Input */}
          <form onSubmit={handleTextSubmit} className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <input
                ref={textInputRef}
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Type a message... (auto-connects)"
                disabled={isSending}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-xl text-sm',
                  'bg-white/5 border border-white/10',
                  'text-white placeholder-white/30',
                  'focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/30',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              />
              <button
                type="submit"
                disabled={!textInput.trim() || isSending}
                className={cn(
                  'px-4 py-2.5 rounded-xl transition-all duration-200',
                  'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white',
                  'hover:from-violet-400 hover:to-fuchsia-400',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:from-slate-600 disabled:to-slate-600'
                )}
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-white/30 mt-2 text-center">
              {isLive
                ? isVoiceEnabled
                  ? "Voice active - you can also type"
                  : "Text mode - click the phone to enable voice"
                : "Type to start chatting (voice optional)"}
            </p>
          </form>
        </GlassPanel>
      </motion.div>

      {/* Help Text */}
      <motion.div
        variants={fadeIn}
        className="mt-6 text-center max-w-lg"
      >
        <p className="text-white/40 text-xs">
          <span className="text-violet-400 font-medium">Flexible Input:</span> Type a message to start chatting instantly.
          Click the phone button to enable voice for hands-free conversation with ultra-low latency.
        </p>
      </motion.div>
      
      {/* Mission Panel Modal */}
      <MissionPanel
        isOpen={isMissionPanelOpen}
        onClose={() => setIsMissionPanelOpen(false)}
        onImageUpload={isLive ? sendImage : undefined}
        onDocumentAdded={isLive ? sendDocumentContext : undefined}
      />
    </motion.div>
  );
}

export default LiveVoiceTab;

